// TODO: Fix no-non-null assertions
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { setTrackedChangeContext } from 'common/Prose/Plugins/TrackChanges/utils'
import { canMergeCells } from 'common/Prose/Plugins/tableSelection/tableSelectionUtils'
import { MIN_CELL_WIDTH } from 'common/Prose/constants/tableConstants'
import { preserveSelection } from 'common/Prose/utils'
import { distributeValue } from 'common/Prose/utils/distributeValue'
import { isEmptyNode } from 'common/Prose/utils/isEmptyNode'
import { findTableFromPos, findTableFromSel, isTableCell, isTableSelection } from 'common/Prose/utils/table'
import { BorderSelection } from 'common/Prose/utils/table/BorderSelection'
import { CellSelection } from 'common/Prose/utils/table/CellSelection'
import { TableMap } from 'common/Prose/utils/table/TableMap'
import { BorderProperties, buildBorderAttr, parseBorderAttr } from 'common/Prose/utils/table/borderAttr'
import { borderSelectionsToNodes } from 'common/Prose/utils/table/borderSelectionsToNodes'
import { isTableEmpty } from 'common/Prose/utils/table/isTableEmpty'
import { selectedRect } from 'common/Prose/utils/table/selectionRect'
import { attachConfirmModalToDom } from 'components/ConfirmModal/ConfirmModal'
import { drop, head, isEqual, last, min, omit, sum, sumBy, times, uniq } from 'lodash'
import { schema, tableNodeTypes } from 'prose-core'
import { Fragment, Node as ProseNode } from 'prosemirror-model'
import { EditorState, NodeSelection, TextSelection, Transaction } from 'prosemirror-state'
import { TableMap as PTableMap } from 'prosemirror-tables'
import { findParentNodeOfTypeClosestToPos } from 'prosemirror-utils'
import { type EditorView } from 'prosemirror-view'
import { ourUuid } from 'utils'
import cellSelectionStyles from '../../nodeViews/Table/CellSelectionIndicator.module.scss'
import { deleteNodeByBlockId } from '../deleteNodeByBlockId'
import { Command } from '../types'

export const _addRowAt = (posAtEndOfTableCell_or_TablePos: number, tr: Transaction): Transaction => {
  const tableNode = findTableFromPos(posAtEndOfTableCell_or_TablePos, tr.doc)

  let attrs: ProseNode['attrs'][] = []
  let shouldBeHeaderRow = false
  let rowToCopyAttrs = {}
  if (tableNode === undefined || tableNode.node.type.name !== 'table')
    throw new Error(
      `Attempted to create a row at position "${posAtEndOfTableCell_or_TablePos}", but that position is not inside a table`,
    )

  const tableMap = new TableMap({ posBeforeTable: tableNode!.pos, node: tableNode!.node })
  let skippedCellsCount = 0
  if (
    posAtEndOfTableCell_or_TablePos !== tableNode!.pos + 1 &&
    posAtEndOfTableCell_or_TablePos !== tableNode!.pos + tableNode!.node.nodeSize - 2
  ) {
    // We are trying to add a row to the middle of the table, so we have to consider rowspans
    const posBeforeFirstCellOfNextRow = posAtEndOfTableCell_or_TablePos + 2
    const rowAboveNewRowIdx = tableMap.getCoordListOfPos(posBeforeFirstCellOfNextRow)[0][0] - 1
    const rowBelowNewRowIdx = rowAboveNewRowIdx + 1
    const cellPosListToCopyFrom = uniq(tableMap.getOrderedPosListInRow(rowAboveNewRowIdx))

    // Check if new row should be a header row
    if (tr.doc.resolve(cellPosListToCopyFrom[0]).nodeAfter?.type === schema.nodes.table_header) {
      const rowBelowCellPosList = uniq(tableMap.getOrderedPosListInRow(rowBelowNewRowIdx))
      shouldBeHeaderRow = tr.doc.resolve(rowBelowCellPosList[0]).nodeAfter?.type === schema.nodes.table_header
    }

    rowToCopyAttrs = tr.doc.resolve(cellPosListToCopyFrom[0]).parent.attrs
    cellPosListToCopyFrom.forEach((posBeforeCell) => {
      const affectedCell = tr.doc.resolve(posBeforeCell).nodeAfter!
      if (affectedCell.attrs.rowspan > 1) {
        // cell must have a rowspan in order to be considered for rowspan expansion
        const affectedCellRowIndexList = tableMap.getCoordListOfPos(posBeforeCell).map((coord) => coord[0])
        if (affectedCellRowIndexList.includes(rowAboveNewRowIdx + 1)) {
          // cell is only considered if the rowspan goes beyond the row we're trying to insert
          tr = tr.setNodeMarkup(posBeforeCell, undefined, {
            ...affectedCell.attrs,
            rowspan: (affectedCell.attrs.rowspan ?? 1) + 1,
          })
          skippedCellsCount += affectedCell.attrs.colspan ?? 1
        } else {
          attrs.push(omit(affectedCell.attrs, 'rowspan'))
          if (affectedCell.attrs.colspan > 1) skippedCellsCount += affectedCell.attrs.colspan - 1
        }
      } else {
        attrs.push(affectedCell.attrs)
        if (affectedCell.attrs.colspan > 1) skippedCellsCount += affectedCell.attrs.colspan - 1
      }
    })
  } else {
    // Adding row to beginning or end of table
    const addingRowToTop = posAtEndOfTableCell_or_TablePos === tableNode!.pos + 1
    const rowToCopy = addingRowToTop ? 0 : tableMap.numRows - 1
    const cellsInRow = uniq(tableMap.getOrderedPosListInRow(rowToCopy))
    shouldBeHeaderRow = addingRowToTop && tr.doc.resolve(cellsInRow[0]).nodeAfter!.type === schema.nodes.table_header
    rowToCopyAttrs = tr.doc.resolve(cellsInRow[0]).parent.attrs
    cellsInRow.forEach((posBeforeCell) => {
      const cell = tr.doc.resolve(posBeforeCell).nodeAfter!
      if (cell.attrs.colspan > 1) skippedCellsCount += cell.attrs.colspan - 1
      attrs.push(omit(cell.attrs, 'rowspan'))
    })
  }
  const numberOfColumnsInTable = tableMap.numCols

  let cells: Array<ProseNode> = []
  for (let index = 0; index < numberOfColumnsInTable - skippedCellsCount; index++) {
    const cell = shouldBeHeaderRow
      ? schema.nodes.table_header.createAndFill(attrs[index])!
      : schema.nodes.table_cell.createAndFill(attrs[index])!

    cells.push(cell)
  }

  const tableRow = schema.nodes.table_row.create(rowToCopyAttrs, cells)

  tr = tr.insert(posAtEndOfTableCell_or_TablePos, tableRow)

  return tr
}

export const _addColumnAt = (posOfTable: number, tr: Transaction, col: number): Transaction => {
  const beforeMappingLength = tr.mapping.maps.length

  let refColumn = col > 0 ? col - 1 : 0
  const table = findTableFromPos(posOfTable + 1, tr.doc)
  if (table) {
    const tableMap = new TableMap({ posBeforeTable: table.pos, node: table.node })
    if (col > tableMap.numCols + 1) return tr

    // When adding a column, we find all columns not manually resized and distribute the widths
    const resizableColumns = tableMap.getColumnWidths().flatMap((width, index) => {
      if (table.node.attrs.resizedColumns.includes(index)) return []
      return [{ width, index }]
    })
    const availableWidth = sumBy(resizableColumns, (column) => column.width)
    let newWidths = distributeValue(availableWidth, resizableColumns.length + 1)
    // distributeValue final value will always be the smallest, if it is less than the minimum
    // width we set all columns not manually resized to the minimum width
    // In this case, table will be wider than the document
    if (last(newWidths)! < MIN_CELL_WIDTH) newWidths = Array(resizableColumns.length + 1).fill(MIN_CELL_WIDTH)
    const changedPosList: number[] = []
    times(resizableColumns.length, (i) => {
      const cellPosList = tableMap.getPosListInColumn(resizableColumns[i].index)
      cellPosList.forEach((cellPos) => {
        const cell = tr.doc.resolve(cellPos).nodeAfter!
        // If colSpan is greater than 1 and we have not yet changed the width of this cell,
        // we add the relevant widths from above array for all the column indices covered by that cell
        if (cell.attrs.colspan > 1) {
          if (!changedPosList.includes(cellPos)) {
            changedPosList.push(cellPos)
            const newCellWidth = sum(times(cell.attrs.colspan, (j) => newWidths[i + j]))
            tr.setNodeMarkup(cellPos, undefined, {
              ...cell.attrs,
              cellWidth: `${newCellWidth}px`,
            })
          }
        } else {
          // If colSpan is 1, we set the width of the cell to the width of the column
          tr.setNodeMarkup(cellPos, undefined, {
            ...cell.attrs,
            cellWidth: `${newWidths[i]}px`,
          })
        }
      })
    })

    // After resizing table, we are ready to add a column
    for (let rowIndex = 0; rowIndex < tableMap.numRows; rowIndex++) {
      const refCellPos = tableMap.getPosAtCoord(refColumn, rowIndex)
      if (!refCellPos) return tr
      const $refCell = tr.doc.resolve(tr.mapping.slice(beforeMappingLength).map(refCellPos))
      const refCell = $refCell.nodeAfter!
      const affectedCellColIndexList = tableMap.getCoordListOfPos(refCellPos).map((coord) => coord[1])
      // If this position falls inside a col-spanning cell, we need to expand the colspan
      if (
        col > 0 &&
        col < tableMap.numCols &&
        affectedCellColIndexList.includes(col) &&
        affectedCellColIndexList.includes(col - 1)
      ) {
        tr.setNodeMarkup(tr.mapping.slice(beforeMappingLength).map(refCellPos), undefined, {
          ...refCell.attrs,
          cellWidth: `${last(newWidths)! + parseInt(refCell.attrs.cellWidth)}px`,
          colspan: refCell.attrs.colspan + 1,
        })
      } else {
        const cellAttrs = { ...omit(refCell.attrs, 'colspan', 'cellWidth'), cellWidth: `${last(newWidths)}px` }
        const insertPos = col === 0 ? tableMap.getPosAtCoord(col, rowIndex) : refCellPos + refCell.content.size + 2
        tr.insert(tr.mapping.slice(beforeMappingLength).map(insertPos!), refCell.type.createAndFill(cellAttrs)!)
      }

      // skip ahead if rowspan > 1
      rowIndex += refCell.attrs.rowspan - 1
    }
  }
  return tr
}

export const addColumnBefore: Command = (state, dispatch) => {
  const { selection } = state
  if (selection instanceof CellSelection || isTableSelection(selection)) {
    const rect = selectedRect(state.selection as CellSelection)
    const table = findTableFromSel(state.selection)
    const tr = _addColumnAt(table!.pos, state.tr, rect.left)
    setTrackedChangeContext(tr, { uuid: ourUuid() })
    dispatch?.(tr)
  }
  return false
}

export const addColumnAfter: Command = (state, dispatch) => {
  const { selection } = state
  if (selection instanceof CellSelection || isTableSelection(selection)) {
    const rect = selectedRect(state.selection as CellSelection)
    const table = findTableFromSel(state.selection)
    const tr = _addColumnAt(table!.pos, state.tr, rect.right + 1)
    setTrackedChangeContext(tr, { uuid: ourUuid() })
    dispatch?.(tr)
  }
  return false
}

export const addRowBefore: Command = (state, dispatch) => {
  const { selection } = state
  if (selection instanceof CellSelection || isTableSelection(selection)) {
    const rect = selectedRect(selection),
      tableMap = rect.tableMap,
      rowIndex = rect.top

    const posListOfRow = tableMap.getOrderedPosListInRow(rowIndex)

    const posBeforeFirstCellOfRow = posListOfRow.find((pos) => {
      const firstRowIndexOfPos = Math.min(...uniq(tableMap.getCoordListOfPos(pos).map((coord) => coord[0])))
      return firstRowIndexOfPos === rowIndex
    })

    // When adding a new top row, _addRowAt takes pos of table,
    // otherwise it takes final pos of cell above where new row is added
    if (posBeforeFirstCellOfRow) {
      const posAtEndOfTableCell_or_TablePos = rowIndex === 0 ? posBeforeFirstCellOfRow - 1 : posBeforeFirstCellOfRow - 2
      const tr = _addRowAt(posAtEndOfTableCell_or_TablePos, state.tr)
      setTrackedChangeContext(tr, { uuid: ourUuid() })
      dispatch?.(tr)
      return true
    }
  }
  return false
}

export const addRowAt: (pos: number) => Command = (pos) => {
  return (state, dispatch) => {
    const tr = _addRowAt(pos, state.tr)
    setTrackedChangeContext(tr, { uuid: ourUuid() })
    dispatch?.(tr)

    return true
  }
}

export const addRowAfter: Command = (state, dispatch) => {
  let { selection, tr } = state
  const tableRowNode = isTableSelection(selection)
    ? { node: selection.$from.nodeAfter!.lastChild!, pos: tr.doc.resolve(selection.to - 2).before() }
    : findParentNodeOfTypeClosestToPos(
        selection instanceof CellSelection ? selection.$toCell : selection.$to,
        schema.nodes.table_row,
      )
  if (!tableRowNode) return false
  const endOfLastTableRow = tableRowNode!.pos + tableRowNode!.node.nodeSize

  tr = _addRowAt(endOfLastTableRow - 1, state.tr)
  if (selection instanceof TextSelection) {
    const paragraphInNewRow = endOfLastTableRow + 3 // 3: enter table row, enter table cell, enter paragraph
    const newCursorPosition = TextSelection.create(tr.doc, paragraphInNewRow)
    tr.setSelection(newCursorPosition)
  }

  setTrackedChangeContext(tr, { uuid: ourUuid() })
  dispatch?.(tr)
  return true
}

export const deleteCellContents: Command = (state, dispatch) => {
  if (state.selection instanceof CellSelection) {
    const tr = _deleteCellContents(state)
    setTrackedChangeContext(tr, { uuid: ourUuid() })
    dispatch?.(tr)

    return true
  }

  return false
}

export const _deleteCellContents = (state: EditorState): Transaction => {
  let { tr, selection } = state

  if (selection instanceof CellSelection) {
    times((selection as CellSelection).$includedCells.length, (includedCellIdx) => {
      if (tr.selection instanceof CellSelection) {
        let $cellPos = (tr.selection as CellSelection).$includedCells[includedCellIdx]
        const from = $cellPos.pos + 1 // +1 to right before content, pos is right before cell
        const to = $cellPos.pos + $cellPos.nodeAfter?.content.size!

        tr = tr.replaceWith(from, to, schema.nodes.paragraph.create({}))
      }
    })
  }

  return tr
}

/**
 * When pressing delete with a row/col selection active, see if those cells are empty
 * If so, delete said row or column and put the selection at the beginning of the table
 * If not, let the next command handle it
 */
export const maybeDeleteColumnOrRow: Command = (state, dispatch) => {
  let { tr, selection } = state
  if (selection instanceof CellSelection) {
    let rowIdx = selection.rowSelectionIndex()
    let colIdx = selection.colSelectionIndex()

    if (rowIdx !== -1 || colIdx !== -1) {
      let isContentEmpty = true
      for (let includedCellIdx = 0; includedCellIdx < selection.$includedCells.length; includedCellIdx++) {
        if (tr.selection instanceof CellSelection) {
          let $cellPos = (tr.selection as CellSelection).$includedCells[includedCellIdx]
          const cellContentSize = $cellPos.nodeAfter?.content.size ?? Infinity
          if (cellContentSize > 2) {
            isContentEmpty = false
            break
          }
        }
      }
      if (isContentEmpty) {
        const tableNode = findTableFromSel(selection)!

        const tableHandlesDomEl = document.querySelector(
          `.${cellSelectionStyles.CellSelectionIndicator}`,
        )?.parentElement
        if (tableHandlesDomEl) {
          tableHandlesDomEl.classList.add(cellSelectionStyles.deleting)
          setTimeout(() => {
            tableHandlesDomEl.classList.remove(cellSelectionStyles.deleting)

            tr.setSelection(TextSelection.create(state.doc, tableNode.pos + 1))
            setTrackedChangeContext(tr, { uuid: ourUuid() })
            if (rowIdx !== -1) {
              dispatch?.(removeRowAt(rowIdx)(tr))
            }
            if (colIdx !== -1) {
              dispatch?.(removeColumnAt(colIdx)(tr))
            }
          }, 300)
        }
        return true
      }
    }
  }
  return false
}

export const removeColumnAt = (columnIndex: number) => (tr: Transaction) => {
  const tableNode = findTableFromSel(tr.selection)
  if (tableNode) {
    // get updated table from transaction
    const updatedTable = tr.doc.resolve(tableNode.pos).nodeAfter
    if (updatedTable) {
      const tableMap = new TableMap({ posBeforeTable: tableNode.pos, node: updatedTable })
      const colPosList = uniq(tableMap.getPosListInColumn(columnIndex))
      const colWidth = tableMap.getColumnWidths()[columnIndex]
      const beforeMappingLength = tr.mapping.maps.length

      colPosList.forEach((pos) => {
        const mappedPos = tr.mapping.slice(beforeMappingLength).map(pos)
        const $cell = tr.doc.resolve(mappedPos)
        const cellNode = $cell.nodeAfter!
        if (!isTableCell(cellNode)) throw new Error('Table cell not found')
        if (cellNode.attrs.colspan > 1) {
          tr.setNodeMarkup(mappedPos, undefined, {
            ...cellNode.attrs,
            colspan: cellNode.attrs.colspan - 1,
            cellWidth: `${parseInt(cellNode.attrs.cellWidth) - colWidth}px`,
          })
        } else {
          tr.delete(mappedPos, mappedPos + $cell.nodeAfter!.nodeSize)
        }
      })
    }
  }
  return tr
}

export const removeRowAt = (rowIndex: number) => (tr: Transaction) => {
  let tableNode = findTableFromSel(tr.selection)

  if (tableNode) {
    // get updated table from transaction
    const updatedTable = tr.doc.resolve(tableNode.pos).nodeAfter
    if (updatedTable) {
      const tablePos = tableNode.pos + 1
      const tableMap = new TableMap({ posBeforeTable: tableNode.pos, node: updatedTable })
      const pMap = PTableMap.get(updatedTable)
      const rowPosList = tableMap.getOrderedPosListInRow(rowIndex)
      let rowPos: number | undefined
      let index = 0
      updatedTable.descendants((node, pos, parent) => {
        if (rowPos) return false
        if (index === rowIndex) {
          rowPos = pos + tablePos
        }
        index++
        return false
      })
      if (rowPos === undefined) throw new Error('Row not found')
      const beforeMappingLength = tr.mapping.maps.length

      uniq(rowPosList).forEach((pos) => {
        const mappedPos = tr.mapping.slice(beforeMappingLength).map(pos)
        const $cell = tr.doc.resolve(mappedPos)
        const cellNode = $cell.nodeAfter!
        if (!isTableCell(cellNode)) throw new Error('Table cell not found')
        if (cellNode.attrs.rowspan > 1) {
          const topRowOfCell = min(tableMap.getCoordListOfPos(pos).map(([row, col]) => row))
          // if the cell is in the row we're removing, insert new cell in next row with rowspan - 1
          if (topRowOfCell === rowIndex) {
            const newCell = schema.nodes.table_cell.create(
              {
                ...cellNode.attrs,
                rowspan: cellNode.attrs.rowspan - 1,
              },
              cellNode.content,
            )
            tr.delete(mappedPos, mappedPos + $cell.nodeAfter!.nodeSize)
            const unmappedPos = pMap.positionAt(rowIndex + 1, rowPosList.indexOf(pos), updatedTable)
            const newCellPos = tr.mapping.slice(beforeMappingLength).map(unmappedPos + tablePos)
            tr.insert(newCellPos, newCell)
          } else {
            // else just decrease rowspan
            tr.setNodeMarkup(mappedPos, undefined, { ...cellNode.attrs, rowspan: cellNode.attrs.rowspan - 1 })
          }
        } else {
          tr.delete(mappedPos, mappedPos + $cell.nodeAfter!.nodeSize)
        }
      })
      const mappedRowPos = tr.mapping.slice(beforeMappingLength).map(rowPos)
      tr.delete(mappedRowPos, mappedRowPos + 1)
    }
  }
  return tr
}

export const deleteColumnsInSelection: Command = (state, dispatch) => {
  let { tr, selection } = state
  if (isTableSelection(selection)) {
    deleteTable(state, dispatch)
  }
  if (selection instanceof CellSelection) {
    const selectedIndex = selection.colSelectionIndex()
    let includedColumns: number[]
    const tableNode = findTableFromSel(selection)
    if (!tableNode) return false
    const tableMap = new TableMap({ posBeforeTable: tableNode.pos, node: tableNode.node })
    if (selectedIndex !== -1) {
      includedColumns = [selectedIndex]
    } else {
      includedColumns = uniq(
        selection.$includedCells
          .map(($c) => tableMap.getCoordListOfPos($c.pos))
          .flat()
          .map((coord) => coord[1])
          .sort(),
      )
    }
    const deleteWholeTable = tableMap.numCols === includedColumns.length
    if (deleteWholeTable) {
      tr.delete(tableNode.pos, tableNode.pos + tableNode.node.nodeSize)
    } else {
      includedColumns.forEach((colIdx, i) => {
        tr = removeColumnAt(colIdx - i)(tr)
      })

      tr.setSelection(CellSelection.columnSelection(tr.doc.resolve(tableNode.pos), Math.max(includedColumns[0] - 1, 0)))
    }

    setTrackedChangeContext(tr, { uuid: ourUuid() })
    dispatch?.(tr)
    return true
  }
  return false
}

export const deleteRowsInSelection: Command = (state, dispatch) => {
  let { tr, selection } = state
  if (isTableSelection(selection)) {
    deleteTable(state, dispatch)
  }
  if (selection instanceof CellSelection) {
    const selectedIndex = selection.rowSelectionIndex()
    let includedRows: number[]
    const tableNode = findTableFromSel(selection)
    if (!tableNode) return false
    const tableMap = new TableMap({ posBeforeTable: tableNode.pos, node: tableNode.node })
    if (selectedIndex !== -1) {
      includedRows = [selectedIndex]
    } else {
      includedRows = uniq(
        selection.$includedCells
          .map(($c) => tableMap.getCoordListOfPos($c.pos))
          .flat()
          .map((coord) => coord[0])
          .sort(),
      )
    }
    const deleteWholeTable = tableMap.numRows === includedRows.length
    if (deleteWholeTable) {
      tr.delete(tableNode.pos, tableNode.pos + tableNode.node.nodeSize)
    } else {
      includedRows.forEach((rowIdx, i) => {
        tr = removeRowAt(rowIdx - i)(tr)
      })

      tr.setSelection(CellSelection.rowSelection(tr.doc.resolve(tableNode.pos), Math.max(includedRows[0] - 1, 0)))
    }

    setTrackedChangeContext(tr, { uuid: ourUuid() })
    dispatch?.(tr)
    return true
  }
  return false
}

export const mergeCells: Command = (state, dispatch) => {
  const { selection } = state
  if (!(selection instanceof CellSelection) || !canMergeCells(selection)) return false

  if (dispatch) {
    const rect = selectedRect(selection),
      { height, width } = rect

    let content = Fragment.empty,
      { tr } = state

    const firstCell = head(selection.$includedCells)!
    const mergedPos = firstCell.pos
    const mergedCell = firstCell.nodeAfter!

    // Sum cell widths along top of 'rect' being merged to calculate new width
    const posList = rect.tableMap.getOrderedPosListInRow(rect.top)
    const newWidth =
      sum(
        selection.$includedCells.flatMap((cell) =>
          posList.includes(cell.pos) ? [parseInt(cell.nodeAfter!.attrs.cellWidth)] : [],
        ),
      ) + 'px'

    let includedFullRows: number[] = []
    if (rect.height > 1) {
      rect.rows.forEach((row) => {
        const posListOfRow = rect.tableMap.getOrderedPosListInRow(row)
        if (posListOfRow.every((pos) => selection.$includedCells.map((cell) => cell.pos).includes(pos)))
          includedFullRows.push(row)
      })
    }

    let includedFullCols: number[] = []
    if (rect.width > 1) {
      rect.cols.forEach((col) => {
        const posListOfCol = rect.tableMap.getPosListInColumn(col)
        if (posListOfCol.every((pos) => selection.$includedCells.map((cell) => cell.pos).includes(pos)))
          includedFullCols.push(col)
      })
    }

    drop(selection.$includedCells, 1).forEach(($cell) => {
      const cell = $cell.nodeAfter!
      if (!isEmptyNode(cell)) content = content.append(cell.content)
      let mapped = tr.mapping.map($cell.pos)
      tr.delete(mapped, mapped + cell.nodeSize)
    })
    tr.setNodeMarkup(mergedPos!, undefined, {
      ...mergedCell.attrs,
      colspan: width,
      rowspan: height,
      cellWidth: newWidth,
    })

    if (includedFullRows.length > 1) {
      includedFullRows.forEach((rowIndex, i) => {
        if (rowIndex !== rect.top) {
          tr = removeRowAt(rowIndex + 1 - i)(tr)
        }
      })
    }

    if (includedFullCols.length > 1) {
      includedFullCols.forEach((colIndex, i) => {
        if (colIndex !== rect.left) {
          tr = removeColumnAt(colIndex + 1 - i)(tr)
        }
      })
    }

    if (content.size) {
      let end = mergedPos + 1 + mergedCell.content.size
      let start = isEmptyNode(mergedCell) ? mergedPos + 1 : end
      tr.replaceWith(start, end, content)
    }

    const newCellPos = tr.doc.resolve(mergedPos)
    tr.setSelection(new CellSelection(newCellPos, newCellPos))
    dispatch(tr)
  }
  return true
}

export const resizeWidth =
  (colIndex: number, newOffset: number, posBeforeTable: number): Command =>
  (state, dispatch) => {
    let { tr } = state
    const tableNode = findTableFromPos(posBeforeTable + 1, state.doc)!
    const tableMap = new TableMap({ posBeforeTable: tableNode!.pos, node: tableNode!.node })
    let resizedColumns = new Set<number>([colIndex])
    // If there is a column after the index you are resizing,
    // it's width changes inversely to the other: size of table only changes then if you resize the last column
    if (tableMap.numCols > colIndex + 1) {
      resizedColumns.add(colIndex + 1)
      const nextColPosList = tableMap.getPosListInColumn(colIndex + 1)
      nextColPosList.forEach((posBeforeCell) => {
        const affectedCell = state.doc.resolve(posBeforeCell + 1).parent
        if (affectedCell.attrs.colspan > 1) {
          const colsInCell = tableMap.getCoordListOfPos(posBeforeCell).map((coord) => coord[1])
          resizedColumns = new Set([...resizedColumns, ...colsInCell])
          if (colsInCell.includes(colIndex)) return
        }
        // offset could be fraction, floor the new cellWidth to avoid jankiness with borders
        const newWidth = Math.max(MIN_CELL_WIDTH, Math.floor(parseInt(affectedCell.attrs.cellWidth) - newOffset)) + 'px'
        tr.setNodeMarkup(posBeforeCell, undefined, {
          ...affectedCell.attrs,
          cellWidth: newWidth,
        })
      })
    }
    const cellPosList = tableMap.getPosListInColumn(colIndex)
    cellPosList.forEach((posBeforeCell) => {
      const affectedCell = state.doc.resolve(posBeforeCell + 1).parent
      if (affectedCell.attrs.colspan > 1) {
        const colsInCell = tableMap.getCoordListOfPos(posBeforeCell).map((coord) => coord[1])
        resizedColumns = new Set([...resizedColumns, ...colsInCell])
        if (colsInCell.includes(colIndex + 1)) return
      }
      // offset could be fraction, ceil the new cellWidth to complement the previous floor
      const newWidth = Math.max(MIN_CELL_WIDTH, Math.ceil(parseInt(affectedCell.attrs.cellWidth) + newOffset)) + 'px'
      tr.setNodeMarkup(posBeforeCell, undefined, {
        ...affectedCell.attrs,
        cellWidth: newWidth,
      })
    })
    tr.setNodeMarkup(posBeforeTable, undefined, {
      ...tableNode.node.attrs,
      resizedColumns: uniq([...tableNode.node.attrs.resizedColumns, ...resizedColumns]),
    })
    setTrackedChangeContext(tr, { uuid: ourUuid() })
    dispatch?.(tr)
    return true
  }

export const resizeHeight =
  (rowIndex: number, newHeight: string, posBeforeTable: number): Command =>
  (state, dispatch) => {
    let { tr } = state
    const tableNode = findTableFromPos(posBeforeTable + 1, state.doc)!
    const tableMap = new TableMap({ posBeforeTable: tableNode!.pos, node: tableNode!.node })
    const cellPosList = tableMap.getOrderedPosListInRow(rowIndex)
    cellPosList.forEach((posBeforeCell) => {
      const affectedCell = state.doc.resolve(posBeforeCell + 1).parent
      tr.setNodeMarkup(posBeforeCell, undefined, {
        ...affectedCell.attrs,
        cellHeight: newHeight,
      })
    })
    setTrackedChangeContext(tr, { uuid: ourUuid() })
    dispatch?.(tr)
    return true
  }

/**
 * Equally distributes each column so table fills given availableWidth(px)
 */
export const distributeColumns: (availableWidth: number, posBeforeTable: number) => Command = (
  availableWidth,
  posBeforeTable,
) => {
  return (state, dispatch) => {
    let { tr } = state
    const tableNode = findTableFromPos(posBeforeTable + 1, state.doc)!
    const tableMap = new TableMap({ posBeforeTable: tableNode!.pos, node: tableNode!.node })
    const calculatedColumnWidths = distributeValue(availableWidth, tableMap.numCols)
    const changedPosList: number[] = []
    // Similar to logic when adding a column, distribute width across all columns/cells in a table
    times(tableMap.numCols, (colIndex) => {
      const cellPosList = tableMap.getPosListInColumn(colIndex)
      cellPosList.forEach((posBeforeCell) => {
        const $cell = state.doc.resolve(posBeforeCell).nodeAfter!
        // If cell covers multiple columns, sum width of all columns covered by cell
        if ($cell.attrs.colspan > 1) {
          if (!changedPosList.includes(posBeforeCell)) {
            // If cell is already resized, don't resize again
            changedPosList.push(posBeforeCell)
            const columnWidthsInCell = times($cell.attrs.colspan, (i) => calculatedColumnWidths[colIndex + i])
            const newCellWidth = sum(columnWidthsInCell)
            tr.setNodeMarkup(posBeforeCell, undefined, {
              ...$cell.attrs,
              cellWidth: `${newCellWidth}px`,
            })
          }
        } else {
          // Colspan is 1 so width of cell is same as width of column
          tr.setNodeMarkup(posBeforeCell, undefined, {
            ...$cell.attrs,
            cellWidth: `${calculatedColumnWidths[colIndex]}px`,
          })
        }
      })
    })
    // All columns in table have no longer been manually resized
    tr.setNodeMarkup(posBeforeTable, undefined, {
      ...tableNode.node.attrs,
      resizedColumns: [],
    })
    setTrackedChangeContext(tr, { ignore: true })
    preserveSelection(tr, state)
    dispatch?.(tr)

    return true
  }
}

export const maybeDeleteTable = (view: EditorView) => {
  const { state, dispatch } = view
  const { tr, selection } = state
  if (isTableSelection(selection)) {
    const table = state.doc.nodeAt(selection.from)
    if (!table) return false
    if (isTableEmpty(table)) {
      tr.delete(selection.from, selection.to)
      setTrackedChangeContext(tr, { uuid: ourUuid() })
      dispatch?.(tr)
      return true
    }
    attachConfirmModalToDom({
      title: 'Delete Table?',
      prompt: 'You are about to delete the table and all its contents. Are you sure?',
      confirmText: 'Delete',
      onConfirm: () => deleteNodeByBlockId(table.attrs.blockId)(view),
      cancelText: 'Cancel',
    })

    return true
  }
  return false
}

export const deleteTable: Command = (state, dispatch) => {
  let { tr, selection } = state
  if (isTableSelection(selection)) {
    setTrackedChangeContext(tr, { uuid: ourUuid() })
    dispatch?.(tr.delete(selection.from, selection.to))
    return true
  }
  return false
}

export const splitCell: Command = (state, dispatch) => {
  const { tr, selection } = state
  if (!(selection instanceof CellSelection) || selection.$anchorCell.pos !== selection.$headCell.pos) return false
  const cellNode = selection.$anchorCell.nodeAfter!
  if (cellNode.attrs.colspan === 1 && cellNode.attrs.rowspan === 1) return false
  const cellPos = selection.$anchorCell.pos
  const nodeTypes = tableNodeTypes(state.schema)

  const getCellType = ({ node, row, col }: { node: ProseNode; row: number; col: number }) =>
    nodeTypes[node.type.spec.tableRole]

  const rect = selectedRect(selection)

  if (dispatch) {
    const baseAttrs = { ...cellNode.attrs, rowspan: 1, colspan: 1 }

    const table = selection.$anchorCell.node(-1)
    const tableStart = selection.$anchorCell.start(-1)

    const map = PTableMap.get(table)
    const colWidths = rect.tableMap.getColumnWidths()
    let cells = [cellPos]
    for (let row = rect.top; row <= rect.bottom; row++) {
      let pos = map.positionAt(row, rect.left, table)
      if (row === rect.top) pos += cellNode.nodeSize
      for (let col = rect.left, i = 0; col <= rect.right; col++, i++) {
        if (col === rect.left && row === rect.top) continue
        cells.push(tr.mapping.map(pos + tableStart, 1))
        tr.insert(
          last(cells)!,
          getCellType({ node: cellNode, row, col }).createAndFill({ ...baseAttrs, cellWidth: `${colWidths[col]}px` }),
        )
      }
    }
    tr.setNodeMarkup(cellPos, getCellType({ node: cellNode, row: rect.top, col: rect.left }), {
      ...baseAttrs,
      cellWidth: `${colWidths[rect.left]}px`,
    })
    tr.setSelection(
      new CellSelection(
        tr.doc.resolve(selection.$anchorCell.pos),
        tr.doc.resolve(last(cells)!),
        cells.map((pos) => tr.doc.resolve(pos)),
      ),
    )
    dispatch(tr)
  }
  return true
}

export const setAlignment = (alignment: string): Command => {
  return (state, dispatch) => {
    let { selection } = state
    if (selection instanceof CellSelection) {
      const tr = _setCellAttrs('verticalAlign', alignment, state)
      setTrackedChangeContext(tr, { uuid: ourUuid() })
      dispatch?.(tr)
      return true
    } else if (isTableSelection(selection)) {
      const tr = _setCellAttrsOfTable('verticalAlign', alignment, state)
      setTrackedChangeContext(tr, { uuid: ourUuid() })
      dispatch?.(tr)
      return true
    }
    return false
  }
}

/**
 * Set background on selected table or row or cells
 */
export const setBackground = (color: string): Command => {
  return (state, dispatch) => {
    let { tr, selection } = state
    if (selection instanceof CellSelection) {
      const rect = selectedRect(selection)

      let includedFullRows: number[] = []
      rect.rows.forEach((row) => {
        const posListOfRow = rect.tableMap.getOrderedPosListInRow(row)
        if (
          posListOfRow.every((pos) => (selection as CellSelection).$includedCells.map((cell) => cell.pos).includes(pos))
        )
          includedFullRows.push(row)
      })

      if (includedFullRows.length > 0) {
        tr = _setRowAttrs('background', color, state)
      } else {
        tr = _setCellAttrs('background', color, state)
      }

      setTrackedChangeContext(tr, { uuid: ourUuid() })
      dispatch?.(tr)
      return true
    } else if (isTableSelection(selection)) {
      tr = _setTableAttrs('background', color, state)
      setTrackedChangeContext(tr, { uuid: ourUuid() })
      dispatch?.(tr)
      return true
    }
    return false
  }
}

export const setBorderAttr = (props: Partial<BorderProperties>): Command => {
  const newBorderString = (oldBorderString: string) => {
    const oldBorderProperties = parseBorderAttr(oldBorderString)
    const newBorderProperties = { ...oldBorderProperties, ...props }
    return buildBorderAttr(newBorderProperties)
  }

  return (state, dispatch) => {
    let { tr, selection } = state
    if (selection instanceof CellSelection || isTableSelection(selection)) {
      const rect = selectedRect(selection),
        tableMap = rect.tableMap
      const cellCoordsAbove =
        rect.top === 0 ? [] : times(rect.cols.length, (index) => [rect.top - 1, index + rect.left])
      const cellCoordsBelow =
        rect.bottom === rect.tableMap.numRows - 1
          ? []
          : times(rect.cols.length, (index) => [rect.bottom + 1, index + rect.left])
      const cellCoordsLeft =
        rect.left === 0 ? [] : times(rect.rows.length, (index) => [index + rect.top, rect.left - 1])
      const cellCoordsRight =
        rect.right === rect.tableMap.numCols - 1
          ? []
          : times(rect.rows.length, (index) => [index + rect.top, rect.right + 1])

      cellCoordsAbove.forEach(([row, col]) => {
        const pos = tableMap!.getPosAtCoord(col, row)!
        const $cell = tr.doc.resolve(pos)
        tr.setNodeMarkup(pos, undefined, {
          ...$cell.nodeAfter!.attrs,
          borderBottom: newBorderString($cell.nodeAfter!.attrs.borderBottom),
        })
      })
      cellCoordsBelow.forEach(([row, col]) => {
        const pos = tableMap!.getPosAtCoord(col, row)!
        const $cell = tr.doc.resolve(pos)
        tr.setNodeMarkup(pos, undefined, {
          ...$cell.nodeAfter!.attrs,
          borderTop: newBorderString($cell.nodeAfter!.attrs.borderTop),
        })
      })
      cellCoordsLeft.forEach(([row, col]) => {
        const pos = tableMap!.getPosAtCoord(col, row)!
        const $cell = tr.doc.resolve(pos)
        let shouldSetIt = true
        if ($cell.nodeAfter!.attrs.rowspan > 1) {
          const cellToTheRightCoord = [row, col + 1]
          const isCellPresent = rect.selectedCoords.find(
            (coord) => coord[0] === cellToTheRightCoord[0] && coord[1] === cellToTheRightCoord[1],
          )
          if (isCellPresent) {
            const cellToTheRightPos = tableMap!.getPosAtCoord(cellToTheRightCoord[1], cellToTheRightCoord[0])!

            if (cellToTheRightPos > pos) {
              shouldSetIt = false
            }
          }
        }
        if (shouldSetIt) {
          tr.setNodeMarkup(pos, undefined, {
            ...$cell.nodeAfter!.attrs,
            borderRight: newBorderString($cell.nodeAfter!.attrs.borderRight),
          })
        }
      })
      cellCoordsRight.forEach(([row, col]) => {
        const pos = tableMap!.getPosAtCoord(col, row)!
        const $cell = tr.doc.resolve(pos)
        let shouldSetIt = true
        if ($cell.nodeAfter!.attrs.rowspan > 1) {
          // look at the cell to the left
          const cellToTheLeftCoord = [row, col - 1]
          // did we set a border there? <--
          const isCellPresent = rect.selectedCoords.find(
            (coord) => coord[0] === cellToTheLeftCoord[0] && coord[1] === cellToTheLeftCoord[1],
          )
          if (isCellPresent) {
            const cellToTheLeftPos = tableMap.getPosAtCoord(cellToTheLeftCoord[1], cellToTheLeftCoord[0])!
            //   if yes, does it come after this merged cell?
            if (cellToTheLeftPos > pos) {
              shouldSetIt = false
            }
          }
        }
        if (shouldSetIt) {
          tr.setNodeMarkup(pos, undefined, {
            ...$cell.nodeAfter!.attrs,
            borderLeft: newBorderString($cell.nodeAfter!.attrs.borderLeft),
          })
        }
      })

      const $includedCellPosList =
        selection instanceof CellSelection
          ? selection.$includedCells
          : tableMap.getPosBeforeCellList().map((pos) => tr.doc.resolve(pos))

      $includedCellPosList.forEach(($cell) => {
        tr.setNodeMarkup($cell.pos, undefined, {
          ...$cell.nodeAfter!.attrs,
          borderLeft: newBorderString($cell.nodeAfter!.attrs.borderLeft),
          borderRight: newBorderString($cell.nodeAfter!.attrs.borderRight),
          borderTop: newBorderString($cell.nodeAfter!.attrs.borderTop),
          borderBottom: newBorderString($cell.nodeAfter!.attrs.borderBottom),
        })
      })

      setTrackedChangeContext(tr, { uuid: ourUuid() })
      dispatch?.(tr)
      return true
    }
    if (selection instanceof BorderSelection) {
      const tableNode = state.doc.nodeAt(selection.anchor)
      if (tableNode) {
        const borderInfo = borderSelectionsToNodes(tableNode, selection, tr.doc)
        for (const { direction, node, nodePos } of borderInfo) {
          tr.setNodeMarkup(nodePos, undefined, {
            ...node.attrs,
            [direction]: newBorderString(node.attrs[direction]),
          })
        }
        setTrackedChangeContext(tr, { uuid: ourUuid() })
        dispatch?.(tr)
        return true
      }
    }

    return false
  }
}

const _setCellAttrs = (attrName: string, attrValue: string, state: EditorState): Transaction => {
  const { tr, selection } = state
  if (selection instanceof CellSelection) {
    selection.$includedCells.forEach(($cell) => {
      tr.setNodeMarkup($cell.pos, undefined, {
        ...$cell.nodeAfter!.attrs,
        [attrName]: attrValue,
      })
    })
  }
  return tr
}

const _setCellAttrsOfTable = (attrName: string, attrValue: string, state: EditorState): Transaction => {
  const { tr, selection } = state
  if (isTableSelection(selection)) {
    const tableNode = findTableFromPos(selection.from + 1, state.doc)!
    const tableMap = new TableMap({ posBeforeTable: tableNode.pos, node: tableNode.node })
    tableMap.getPosBeforeCellList().forEach((posBeforeCell) => {
      const $cell = state.doc.resolve(posBeforeCell)
      tr.setNodeMarkup(posBeforeCell, undefined, {
        ...$cell.nodeAfter!.attrs,
        [attrName]: attrValue,
      })
    })
  }
  return tr
}

const _setRowAttrs = (attrName: string, attrValue: string, state: EditorState): Transaction => {
  const { tr, selection } = state
  if (selection instanceof CellSelection) {
    selection.$includedCells.forEach(($cell) => {
      tr.setNodeMarkup($cell.pos, undefined, {
        ...$cell.nodeAfter!.attrs,
        [attrName]: null,
      })
      const rowPos = $cell.pos - 1
      const maybeRow = state.doc.resolve(rowPos).nodeAfter
      if (maybeRow && maybeRow.type.spec.tableRole === 'row') {
        tr.setNodeMarkup(rowPos, undefined, {
          ...maybeRow.attrs,
          [attrName]: attrValue,
        })
      }
    })
  }
  return tr
}

const _setTableAttrs = (attrName: string, attrValue: string, state: EditorState): Transaction => {
  const { tr, selection } = state
  if (isTableSelection(selection)) {
    const tablemap = new TableMap({ posBeforeTable: selection.from, node: (selection as NodeSelection).node })
    tablemap.getPosBeforeCellList().forEach((posBeforeCell) => {
      const cell = state.doc.resolve(posBeforeCell).nodeAfter
      tr.setNodeMarkup(posBeforeCell, undefined, {
        ...cell?.attrs,
        [attrName]: null,
      })
    })
    tablemap.getPosListInColumn(0).forEach((posBeforeCell) => {
      const row = state.doc.resolve(posBeforeCell - 1)
      if (row) {
        tr.setNodeMarkup(row.pos, undefined, {
          ...row.nodeAfter!.attrs,
          [attrName]: null,
        })
      }
    })
    tr.setNodeMarkup(selection.from, undefined, {
      ...selection.$from.nodeAfter!.attrs,
      [attrName]: attrValue,
    })
    tr.setSelection(new NodeSelection(tr.doc.resolve(selection.from)))
  }
  return tr
}

export const convertRowsToHeader =
  (posOfTable: number, bottomIncludedRowIdx: number): Command =>
  (state, dispatch) => {
    const { tr } = state
    const table = findTableFromPos(posOfTable + 1, tr.doc)
    if (!table) return false
    const tableMap = new TableMap({ posBeforeTable: table.pos, node: table.node })
    const includedRows = Array.from({ length: bottomIncludedRowIdx + 1 }).map((_, i) => i)
    const includedCells = new Set(includedRows.flatMap((rowIdx) => tableMap.getOrderedPosListInRow(rowIdx)))
    const canConvert = Array.from(includedCells).every((pos) => {
      const $cell = tr.doc.resolve(pos)
      if ($cell.nodeAfter!.type === schema.nodes.table_header) return true
      if ($cell.nodeAfter!.attrs.rowspan > 1) return false
      tr.setNodeMarkup(pos, schema.nodes.table_header, $cell.nodeAfter!.attrs)
      return true
    })
    if (!canConvert) return false
    setTrackedChangeContext(tr, { uuid: ourUuid() })
    dispatch?.(tr)
    return true
  }

export const removeHeadersUpToRow =
  (posOfTable: number, topIncludedRowIdx: number): Command =>
  (state, dispatch) => {
    const { tr } = state
    const table = findTableFromPos(posOfTable + 1, tr.doc)
    if (!table) return false
    const tableMap = new TableMap({ posBeforeTable: table.pos, node: table.node })
    const rowsToStayHeader = Array.from({ length: topIncludedRowIdx }).map((_, i) => i)
    const tableRows = Array.from({ length: tableMap.numRows }).map((_, i) => i)
    const rowsNotHeaders = tableRows.filter((rowIdx) => !rowsToStayHeader.includes(rowIdx))
    const cellsNotHeaders = new Set(rowsNotHeaders.flatMap((rowIdx) => tableMap.getOrderedPosListInRow(rowIdx)))
    const canConvert = Array.from(cellsNotHeaders).every((pos) => {
      const $cell = tr.doc.resolve(pos)
      if ($cell.nodeAfter!.attrs.rowspan > 1) return false
      if ($cell.nodeAfter!.type === schema.nodes.table_header) {
        tr.setNodeMarkup(pos, schema.nodes.table_cell, {
          ...$cell.nodeAfter!.attrs,
          background: null,
        })
      }
      return true
    })
    if (!canConvert) return false
    setTrackedChangeContext(tr, { uuid: ourUuid() })
    dispatch?.(tr)
    return true
  }

export const moveColumn = (initialIndex: number, dropPoint: number, posBeforeTable: number): Command => {
  return (state, dispatch) => {
    const newIndex = dropPoint > initialIndex ? dropPoint - 1 : dropPoint
    if (initialIndex === newIndex) return false // no-op
    const table = findTableFromPos(posBeforeTable + 1, state.doc)
    if (!table) return false
    const tableMap = new TableMap({ posBeforeTable: table.pos, node: table.node })

    // Something very wrong with the proposed move
    if (initialIndex < 0 || initialIndex >= tableMap.numCols || newIndex < 0 || newIndex >= tableMap.numCols)
      return false

    // Moving column between horizontally merged cells
    const dropPointColFragments = tableMap.getColFragments()[dropPoint]
    const isValidDropPoint =
      dropPointColFragments.length === 1 && isEqual(dropPointColFragments[0], [0, tableMap.numRows - 1])
    if (!isValidDropPoint) return false

    // If there are horizontal merged cells in the column, don't allow moving
    // We could allow moving column with horizontal merged cells later if there is a need,
    // just need to add more logic to final column order and probably a better visual that multiple columns would be moved
    const hasHorizontalMergedCells = tableMap.getPosListInColumn(initialIndex).some((pos) => {
      const $cell = state.doc.resolve(pos)
      return $cell.nodeAfter!.attrs.colspan > 1
    })
    if (hasHorizontalMergedCells) return false

    let error = false
    const colOrder = [...Array(tableMap.numCols).keys()]
    const newColOrder = moveElement(colOrder, initialIndex, newIndex)
    let newRows: ProseNode[] = []
    times(tableMap.numRows, (rowIndex) => {
      const copiedRow = table.node.child(rowIndex)
      const row: ProseNode[] = []
      for (let i = 0; i < newColOrder.length; i++) {
        const cellPos = tableMap.getPosAtCoord(newColOrder[i], rowIndex)
        if (cellPos) {
          let shouldCreateCell = true
          const $cell = state.doc.resolve(cellPos)
          const cell = $cell.nodeAfter!
          // if cell has rowspan, look for same cell above, if it exists cell has already been added, skip
          if (cell.attrs.rowspan > 1) {
            const cellAbovePos = tableMap.getPosAtCoord(newColOrder[i], rowIndex - 1)
            if (cellAbovePos === cellPos) shouldCreateCell = false
          }
          if (shouldCreateCell) row.push(cell.type.create(cell.attrs, cell.content))
          if (cell.attrs.colspan > 1) i += cell.attrs.colspan - 1
        } else {
          error = true
        }
      }
      newRows.push(schema.nodes.table_row.create(copiedRow.attrs, row))
    })
    if (error) return false
    const newTable = schema.nodes.table.create(table.node.attrs, newRows)
    const tr = state.tr.replaceWith(table.pos, table.pos + table.node.nodeSize, newTable)
    dispatch?.(tr)
    return true
  }
}

export const moveRow = (initialIndex: number, dropPoint: number, posBeforeTable: number): Command => {
  return (state, dispatch) => {
    const newIndex = dropPoint > initialIndex ? dropPoint - 1 : dropPoint
    if (initialIndex === newIndex) return false // no-op
    const table = findTableFromPos(posBeforeTable + 1, state.doc)
    if (!table) return false
    const tableMap = new TableMap({ posBeforeTable: table.pos, node: table.node })

    // Something very wrong with the proposed move
    if (initialIndex < 0 || initialIndex >= tableMap.numRows || newIndex < 0 || newIndex >= tableMap.numRows)
      return false

    // Moving header row not allowed
    const initialIndexIsHeader =
      state.doc.resolve(tableMap.getOrderedPosListInRow(initialIndex)[0]).nodeAfter?.type === schema.nodes.table_header
    if (initialIndexIsHeader) return false

    // Moving row between vertically merged cells
    const dropPointRowFragments = tableMap.getRowFragments()[dropPoint]
    const isValidDropPoint =
      dropPointRowFragments.length === 1 && isEqual(dropPointRowFragments[0], [0, tableMap.numCols - 1])
    if (!isValidDropPoint) return false

    // Moving row above header not allowed
    const newIndexAboveHeader =
      state.doc.resolve(tableMap.getOrderedPosListInRow(newIndex)[0]).nodeAfter?.type === schema.nodes.table_header
    if (newIndexAboveHeader) return false

    // If there are vertical merged cells in the row, don't allow moving
    // We could allow moving row with vertical merged cells later if there is a need,
    // just need to add more logic to final row order and probably a better visual that multiple rows would be moved
    const hasVerticalMergedCells = tableMap.getOrderedPosListInRow(initialIndex).some((pos) => {
      const $cell = state.doc.resolve(pos)
      return $cell.nodeAfter!.attrs.rowspan > 1
    })
    if (hasVerticalMergedCells) return false

    const rowOrder = [...Array(tableMap.numRows).keys()]
    const newRowOrder = moveElement(rowOrder, initialIndex, newIndex)
    const newRows = newRowOrder.map((rowIndex) => table.node.child(rowIndex))
    const newTable = schema.nodes.table.create(table.node.attrs, newRows)
    const tr = state.tr.replaceWith(table.pos, table.pos + table.node.nodeSize, newTable)
    dispatch?.(tr)
    return true
  }
}

const moveElement = <T>(arr: T[], from: number, to: number) => {
  const newArr = [...arr]
  const element = newArr[from]
  newArr.splice(from, 1)
  newArr.splice(to, 0, element)
  return newArr
}
